import React from 'react';
import {
  HelpTypes,
  ICalendarEventType,
  PermissionsOfAdmin,
} from '@pharmaplan/common';
import { genericFunction } from '../../AvailableWorkshift/useAvailableWorkshiftActions';
import strings from '../../../localization';
import { useAppDispatch } from '../../useAppDispatch';
import {
  ICustomBack,
  IFromScreen,
  renderScreen,
} from '../../../actions/drawerActions';
import { ScreenTypes, UserLabels } from '../../../helpers/Constants';
import { setDialog } from '../../../reducers/dialogReducer';
import UploadResume from '../../../components/Admin/Profile/Pharmacist/PharmacistViewProfile/UploadResume';
import UploadPhoto from '../../../components/Admin/Profile/Pharmacist/PharmacistViewProfile/UploadPhoto/UploadPhoto';
import useAdminPermissions from '../useAdminPermissions';
import UploadDiploma from '../../../components/Admin/Profile/Pharmacist/PharmacistViewProfile/UploadDiploma';
import usePharmaPlanInvitation from './usePharmaPlanInvitation';

const diplomaKey = 'diplomaKey';
interface IMenuActions {
  canChangeParameters: boolean;
  userType: HelpTypes;
  editAction: genericFunction;
  uploadPhotoAction: genericFunction;
  uploadResumeAction: genericFunction;
  uploadDiplomaAction: genericFunction;
  geographicAvailabilityAction: genericFunction;
  pharmaPlanInvitationAction: {
    key: string;
    label: string;
    onClick: () => void;
  } | null;
}
interface IGeographicAvailability {
  type: ICalendarEventType;
  back?: ICustomBack;
}

export const menuActions = ({
  canChangeParameters,
  userType,
  editAction,
  uploadDiplomaAction,
  uploadPhotoAction,
  uploadResumeAction,
  geographicAvailabilityAction,
  pharmaPlanInvitationAction,
}: IMenuActions) => {
  let arr = [
    {
      key: '0',
      label: strings.edit,
      onClick: editAction,
    },
    {
      key: '2',
      label: strings.uploadPicture,
      onClick: uploadPhotoAction,
    },
    {
      key: '3',
      label: strings.uploadResume,
      onClick: uploadResumeAction,
    },
    {
      key: diplomaKey,
      label: strings.uploadDiploma,
      onClick: uploadDiplomaAction,
    },
    {
      key: '5',
      label: strings.formatString(
        strings.userGeographicAvailability,
        UserLabels()?.[userType ?? HelpTypes.pharmacist] ?? strings.pharmacist,
      ),
      onClick: geographicAvailabilityAction,
    },
    ...(pharmaPlanInvitationAction ? [pharmaPlanInvitationAction] : []),
  ];

  if (`${userType}` !== HelpTypes.technicians) {
    arr = arr.filter((item) =>
      item.key !== diplomaKey);
  }

  return canChangeParameters ? arr : arr.slice(1);
};

const usePharmacistProfileViewDetails = (
  userType: HelpTypes,
  fromScreen?: IFromScreen,
  hasOwner?: boolean,
  pharmacistId = '',
  canSendInvite = false,
  canResumePorting = false,
  lastPPInviteSentAt: string | null = null,
) => {
  const dispatch = useAppDispatch();

  const { can } = useAdminPermissions();
  const canChangeParameters = can(PermissionsOfAdmin.ChangeParameters);
  const { action: pharmaPlanInvitationAction } = usePharmaPlanInvitation({
    personnelId: pharmacistId,
    userType,
    canSendInvite,
    canResumePorting,
    lastPPInviteSentAt,
  });

  const showPictureUploadModal = () =>
    () => {
      dispatch(
        setDialog({
          maxWidth: 'xs',
          showCloseButton: true,
          Component: <UploadPhoto id={pharmacistId} />,
          heading: {
            title: strings.uploadPicture,
          },
        }),
      );
    };

  const showResumeUploadModal = () =>
    () => {
      dispatch(
        setDialog({
          maxWidth: 'xs',
          showCloseButton: true,
          Component: <UploadResume pharmacistId={pharmacistId} />,
          heading: {
            title: strings.uploadResume,
          },
        }),
      );
    };

  const showDiplomaUploadModal = () =>
    () => {
      dispatch(
        setDialog({
          maxWidth: 'xs',
          showCloseButton: true,
          Component: <UploadDiploma professionalId={pharmacistId} />,
          heading: {
            title: strings.uploadDiploma,
          },
        }),
      );
    };

  const goToEditPharmacistProfile = ({
    type,
    back,
  }: {
    type: ICalendarEventType;
    back?: ICustomBack;
  }) => {
    dispatch(
      renderScreen({
        screenNumber: 4,
        screenType: ScreenTypes.profile,
        pharmacistId,
        fromScreen,
        type,
        hasOwner,
        back,
      }),
    );
  };

  const goToGeographicAvailability = ({ type, back }: IGeographicAvailability) =>
    () => {
      dispatch(
        renderScreen({
          screenNumber: 5,
          screenType: ScreenTypes.profile,
          fromScreen,
          hasOwner,
          pharmacistId,
          type,
          back,
        }),
      );
    };

  return {
    actions: (id: string, type: ICalendarEventType, back?: ICustomBack) =>
      menuActions({
        userType,
        uploadDiplomaAction: showDiplomaUploadModal(),
        canChangeParameters,
        editAction: () =>
          goToEditPharmacistProfile({ type, back }),
        uploadPhotoAction: showPictureUploadModal(),
        uploadResumeAction: showResumeUploadModal(),
        geographicAvailabilityAction: goToGeographicAvailability({
          type,
          back,
        }),
        pharmaPlanInvitationAction,
      }),
  };
};

export default usePharmacistProfileViewDetails;
